<template>
  <div>
    <header class="db-page-head">
      <div>
        <h2 class="db-page-title">Roles y permisos</h2>
        <p class="db-page-subtitle">Cada usuario tiene un rol, y el rol decide qué secciones del panel puede gestionar.</p>
      </div>
      <div class="db-actions">
        <button type="button" class="db-btn" @click="abrir(null)">
          <span class="material-symbols-outlined">add</span> Nuevo rol
        </button>
      </div>
    </header>

    <div v-if="loading" class="vr-grid">
      <div v-for="n in 4" :key="n" class="db-skeleton" style="height: 180px"></div>
    </div>

    <div v-else class="vr-grid">
      <article v-for="r in roles" :key="r.id" class="db-card vr-card">
        <div class="vr-head">
          <span class="vr-icon" :class="{ 'is-super': r.es_superadmin }">
            <span class="material-symbols-outlined">{{ r.es_superadmin ? "shield_person" : r.protegido ? "admin_panel_settings" : "badge" }}</span>
          </span>
          <div class="vr-title-wrap">
            <h3 class="vr-title">{{ r.etiqueta }}</h3>
            <span class="db-hint">
              {{ r.usuarios_count }} {{ r.usuarios_count === 1 ? "usuario" : "usuarios" }}
              <template v-if="r.protegido"> · Rol del sistema</template>
            </span>
          </div>
        </div>

        <p class="vr-desc">{{ r.descripcion || "Sin descripción." }}</p>

        <ul class="vr-permisos">
          <li v-if="r.es_superadmin" class="db-badge db-badge--gold">Todos los permisos</li>
          <template v-else>
            <li v-for="p in r.permisos" :key="p" class="db-badge db-badge--info">{{ nombrePermiso(p) }}</li>
            <li v-if="!r.permisos.length" class="db-badge">Sin permisos</li>
          </template>
        </ul>

        <div class="vr-actions">
          <button type="button" class="db-btn db-btn--ghost db-btn--sm" @click="abrir(r)">
            <span class="material-symbols-outlined">{{ r.acciones.editar ? "edit" : "visibility" }}</span>
            {{ r.acciones.editar ? "Editar" : "Ver" }}
          </button>
          <button
            v-if="!r.protegido"
            type="button"
            class="db-icon-btn db-icon-btn--danger"
            :disabled="!r.acciones.eliminar"
            :title="r.acciones.eliminar ? 'Eliminar rol' : 'Tiene usuarios asignados'"
            :aria-label="`Eliminar el rol ${r.etiqueta}`"
            @click="eliminar(r)"
          >
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      </article>
    </div>

    <modal-rol modal-id="modalRol" :rol="seleccionado" :permisos="permisos" @guardado="alGuardar" />
  </div>
</template>

<script>
import ModalRol from "@/components/dashboard/modals/ModalRol.vue";
import axios from "@/directives/axios.js";
import { avisar, confirmar, avisarError } from "@/directives/dbDialogs.js";

export default {
  name: "VistaRoles",

  components: { ModalRol },

  data() {
    return {
      roles: [],
      permisos: [],
      seleccionado: null,
      loading: true,
    };
  },

  methods: {
    nombrePermiso(name) {
      const p = this.permisos.find((x) => x.name === name);
      return p ? p.nombre : name;
    },

    async cargar() {
      try {
        const [roles, permisos] = await Promise.all([axios.get("/roles"), axios.get("/permisos")]);
        this.roles = roles.data.roles || [];
        this.permisos = permisos.data.permisos || [];
      } catch (error) {
        avisarError(this, error, "No se pudieron cargar los roles.");
      } finally {
        this.loading = false;
      }
    },

    abrir(rol) {
      this.seleccionado = rol;
      this.$nextTick(() => this.$bvModal.show("modalRol"));
    },

    async alGuardar(roles) {
      if (roles) this.roles = roles;
      else await this.cargar();
      // Si se ha tocado el rol propio, los permisos del usuario actual pueden haber cambiado.
      this.$store.dispatch("cargarUsuario").catch(() => {});
    },

    async eliminar(rol) {
      const ok = await confirmar(this, `Se eliminará el rol «${rol.etiqueta}».`, {
        titulo: "Eliminar rol",
        ok: "Eliminar",
        peligro: true,
      });
      if (!ok) return;

      try {
        const response = await axios.delete(`/roles/${rol.id}`);
        this.roles = response.data.roles || this.roles.filter((r) => r.id !== rol.id);
        avisar(this, `Se ha eliminado el rol «${rol.etiqueta}».`, "success", "Rol eliminado");
      } catch (error) {
        avisarError(this, error, `No se pudo eliminar el rol «${rol.etiqueta}».`);
        await this.cargar();
      }
    },
  },

  mounted() {
    this.cargar();
  },
};
</script>

<style scoped>
.vr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.vr-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
}

.vr-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.vr-icon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: var(--sd-primary-soft);
  color: var(--sd-primary);
}

.vr-icon.is-super {
  background: var(--sd-gold-soft);
  color: var(--sd-gold);
}

.vr-title-wrap {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vr-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--sd-text);
}

.vr-desc {
  margin: 0;
  font-size: 0.9rem;
  color: var(--sd-muted);
}

.vr-permisos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.vr-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid var(--sd-border);
}
</style>
